import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../api/client.js';
import type {
  CompletionRecord,
  PreApprovalStatus,
  ProposalResponse,
  RepairSchedule,
  WorkOrderDetail,
} from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS, ROLE_CODES } from '../../shared/permissions.js';
import { formatCurrency } from '../lib/format.js';
import { StepsCard, type NextStepTab, type Step, type StepPlan } from './NextStepsPanel.js';

/**
 * The USPS counterpart of the VGS checklist: what is waiting on the reader, what each
 * decision does, and what VGS will do after it, so an approver never has to guess.
 */
export function CustomerNextStepsPanel({
  workOrder,
  onNavigate,
}: {
  workOrder: WorkOrderDetail;
  onNavigate: (tab: NextStepTab) => void;
}) {
  const { can, user } = useSession();
  const id = workOrder.id;
  const proposals = useQuery({
    queryKey: ['proposals', id],
    queryFn: () => apiRequest<ProposalResponse>(`/api/work-orders/${id}/proposals`),
    enabled: can(PERMISSIONS.PROPOSAL_VIEW),
  });
  const schedule = useQuery({
    queryKey: ['schedule', id],
    queryFn: () =>
      apiRequest<{ schedule: RepairSchedule | null }>(`/api/work-orders/${id}/schedule`).then(
        (result) => result.schedule,
      ),
  });
  const completion = useQuery({
    queryKey: ['completion', id],
    queryFn: () =>
      apiRequest<{ completion: CompletionRecord | null }>(`/api/work-orders/${id}/completion`).then(
        (result) => result.completion,
      ),
  });
  const preApproval = useQuery({
    queryKey: ['pre-approval', id],
    queryFn: () => apiRequest<PreApprovalStatus>(`/api/work-orders/${id}/pre-approval`),
  });

  const plan = buildCustomerPlan({
    workOrder,
    proposals: proposals.data,
    schedule: schedule.data,
    completion: completion.data,
    preApproval: preApproval.data,
    mayDecide: can(PERMISSIONS.PROPOSAL_APPROVE),
    isContractingOfficer: user?.roleCodes.includes(ROLE_CODES.CUSTOMER_CO) ?? false,
  });
  if (!plan) return null;
  return <StepsCard title="What happens next" plan={plan} onNavigate={onNavigate} />;
}

function buildCustomerPlan({
  workOrder,
  proposals,
  schedule,
  completion,
  preApproval,
  mayDecide,
  isContractingOfficer,
}: {
  workOrder: WorkOrderDetail;
  proposals?: ProposalResponse;
  schedule?: RepairSchedule | null;
  completion?: CompletionRecord | null;
  preApproval?: PreApprovalStatus;
  mayDecide: boolean;
  isContractingOfficer: boolean;
}): StepPlan | null {
  const versions = proposals?.versions ?? [];
  const submitted = versions.find((version) => version.status === 'submitted');
  const finalInvoices = versions.filter((version) => version.kind === 'final_invoice');
  const approvedInvoice = finalInvoices.some((version) => version.status === 'approved');
  const authorizedToProceed = versions.some(
    (version) => version.kind === 'proceed_authorization' && version.status === 'approved',
  );
  const emergency = workOrder.emergencyCallout;

  const pendingException = preApproval?.exceptions.find((item) => item.status === 'pending');
  const responseLineDeclined =
    preApproval?.determination?.outcome === 'declined' &&
    !preApproval.determination.coDiscretionApproved;

  // Items a Contracting Officer may be asked for regardless of the proposal stage.
  const coItems: Step[] = [];
  if (isContractingOfficer && pendingException) {
    coItems.push({
      label: 'Decide the competition exception VGS requested',
      hint: `VGS obtained ${pendingException.quotesObtained} quote(s) and asked to proceed without the full three. Approve or reject it on the Proposal tab; VGS cannot submit the price until this is decided.`,
      done: false,
      tab: 'proposals',
    });
  }
  if (isContractingOfficer && responseLineDeclined) {
    coItems.push({
      label: 'Decide whether to approve at Contracting Officer discretion',
      hint: 'The Facilities Response Line declined this work. It can still proceed only if you grant discretion on the Proposal tab.',
      done: false,
      tab: 'proposals',
    });
  }

  switch (workOrder.status) {
    case 'submitted':
      return {
        stage: 'Submitted',
        intro:
          'Your request has reached VGS. VGS acknowledges it, investigates, and prices the work. Nothing is needed from USPS yet; you will be emailed when a proposal is ready for approval.',
        steps: coItems,
      };

    case 'under_vgs_review':
    case 'estimate_in_progress':
    case 'revision_requested':
      return {
        stage: workOrder.status === 'revision_requested' ? 'Revision in progress' : 'Being priced',
        intro:
          workOrder.status === 'revision_requested'
            ? 'VGS is preparing the revision you asked for. You will be emailed when it is resubmitted.'
            : emergency
              ? 'This was an emergency call-out: the work was already performed and VGS is pricing it for after-the-fact approval. You will be emailed when the proposal is submitted.'
              : 'VGS is preparing the proposal. You will be emailed when it is submitted for approval.',
        steps: coItems,
      };

    case 'awaiting_customer_approval': {
      if (!submitted) return { stage: 'Awaiting approval', steps: coItems };
      const invoice = submitted.kind === 'final_invoice';
      const authorization = submitted.kind === 'proceed_authorization';
      const amount = authorization
        ? submitted.totalCents > 0
          ? `${formatCurrency(submitted.totalCents)} not-to-exceed`
          : 'no-estimate'
        : formatCurrency(submitted.totalCents);
      const steps: Step[] = [...coItems];
      const whatApproveDoes = invoice
        ? 'Approve confirms the actual invoice against the approved estimate; VGS then closes the work order.'
        : authorization
          ? 'Approve authorizes VGS to dispatch a subcontractor now, before a quote exists; the three-quote rule is waived. The actual invoice comes back to you for approval before the work order can close.'
          : emergency
            ? 'Approve ratifies the cost of work already performed; VGS then records completion and closes the work order.'
            : 'Approve authorizes VGS to schedule and perform the work at this price; nothing can start until then.';
      const whatRejectDoes =
        'Request revision sends it back to VGS with your reason to re-price; Reject ends this proposal with your reason. Either way VGS is emailed and USPS pays nothing.';

      if (submitted.awaitingCorReview) {
        steps.push({
          label: submitted.viewerMayEndorse
            ? `Endorse or return the ${amount} proposal (COR review)`
            : `Waiting for the COR to review the ${amount} proposal`,
          hint: submitted.viewerMayEndorse
            ? `Above the COR limit the Contracting Officer gives final approval. Your endorsement sends it to the CO with your comment; ${whatRejectDoes}`
            : 'The Contracting Officer is emailed only after the COR endorses it.',
          done: false,
          tab: 'proposals',
        });
        steps.push({
          label: 'Contracting Officer final approval',
          hint: whatApproveDoes,
          done: false,
          tab: 'proposals',
          optional: true,
        });
      } else {
        steps.push({
          label: submitted.viewerMayApprove
            ? `Approve, request revision, or reject the ${amount} ${invoice ? 'final invoice' : authorization ? 'authorization to proceed' : 'proposal'}`
            : `Waiting for ${submitted.requiredAuthorityLabel ?? 'the approver'} to decide on ${amount}`,
          hint: submitted.viewerMayApprove
            ? `${whatApproveDoes} ${whatRejectDoes}`
            : mayDecide
              ? 'This amount is above your approval authority.'
              : undefined,
          done: false,
          tab: 'proposals',
        });
      }
      return {
        stage: invoice ? 'Final invoice awaiting approval' : 'Awaiting approval',
        intro: emergency
          ? 'Emergency call-out: the work was performed before it could be priced. Approval here ratifies the cost; the three-quote requirement is waived.'
          : undefined,
        steps,
      };
    }

    case 'procurement':
      return {
        stage: 'Approved — VGS procurement',
        intro: `You approved ${
          workOrder.approvedTotalCents === null
            ? 'the price'
            : formatCurrency(workOrder.approvedTotalCents)
        }. VGS is completing its internal purchasing paperwork and will then schedule the repair; you will be notified of the date.`,
        steps: [
          ...coItems,
          {
            label: 'VGS schedules the repair',
            done: Boolean(schedule),
            tab: 'execution',
          },
        ],
      };

    case 'approved':
      if (workOrder.kind === 'supplies') {
        return {
          stage: 'Approved',
          intro: `You approved ${
            workOrder.approvedTotalCents === null
              ? 'the price'
              : formatCurrency(workOrder.approvedTotalCents)
          }. VGS now orders the items; you will be notified when they are received.`,
          steps: [...coItems, { label: 'VGS orders and receives the items', done: false }],
        };
      }
      return {
        stage: 'Approved',
        intro: `You approved ${
          workOrder.approvedTotalCents === null
            ? 'the price'
            : formatCurrency(workOrder.approvedTotalCents)
        }. VGS now schedules the repair; you will be notified of the date.`,
        steps: [
          ...coItems,
          {
            label: 'VGS schedules the repair',
            done: Boolean(schedule),
            tab: 'execution',
          },
        ],
      };

    case 'repair_scheduled':
      if (workOrder.kind === 'supplies') {
        return {
          stage: 'Ordered',
          intro: 'VGS has ordered the items; you will be notified when they are received.',
          steps: [],
        };
      }
      return {
        stage: 'Scheduled',
        intro:
          'The repair date is set. VGS will mark work started on the day; nothing is needed from USPS.',
        steps: [],
      };

    case 'work_in_progress':
      return {
        stage: 'Work in progress',
        intro: 'VGS is on site. You will be notified when the work is completed.',
        steps: [
          {
            label: 'VGS records completion',
            done: Boolean(completion),
            tab: 'execution',
          },
        ],
      };

    case 'completed': {
      const steps: Step[] = [];
      if (authorizedToProceed || finalInvoices.length > 0) {
        steps.push({
          label: approvedInvoice
            ? 'Final invoice approved'
            : 'VGS submits the actual invoice for your approval',
          hint: 'You authorized this work against a not-to-exceed amount; the actual price must be approved before VGS can close the work order. You will be emailed when it is submitted.',
          done: approvedInvoice,
          tab: 'proposals',
        });
      }
      steps.push({
        label: 'VGS closes the work order',
        hint: 'Completion details and the invoice number are on this page for your records.',
        done: false,
      });
      return { stage: 'Completed', intro: 'The work is done.', steps };
    }

    case 'on_hold':
      return {
        stage: 'On hold',
        intro: workOrder.holdReason
          ? `VGS placed this on hold: ${workOrder.holdReason}`
          : 'VGS placed this on hold and will resume it.',
        steps: [],
      };

    case 'rejected':
      return {
        stage: 'Rejected',
        intro: 'The proposal was rejected. VGS may prepare a new proposal or cancel the request.',
        steps: [],
      };

    default:
      return null;
  }
}
